/* ===================== ASTROLOGER My Profile =====================
   Role-specific My Profile screen for astrologer accounts, rendered
   through TAB_ROLE_COMPONENTS in tabs/index.js — the seeker profile
   (SettingsTab) stays untouched for every other role.

   This screen renders the onboarding wizard's OWN step panels
   (@astro/astrologer-forms — the exact components, fields, widgets
   and validation the /astrologer-register page uses) as stacked
   sections on one page. Agreement (declarations + signature) is a
   one-time application step and is skipped; password, photo and
   document inputs are hidden via the panels' profile-mode props.

   Prefill: GET /user + GET /astrologer/profile through useForm's
   `values` option (re-fills whenever the reads land; dirty fields
   are preserved). Save: PUT /astrologer/profile — a partial update,
   upstream validates every field with `sometimes`. */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { FormProvider, useForm } from '@astro/forms'
import {
  STEPS, buildProfilePrefill, buildProfileUpdatePayload, unwrapAstrologerProfile,
  StepBasicInfo, StepProfile, StepEducation, StepProfessional,
  StepAvailability, StepKyc, StepBank,
} from '@astro/astrologer-forms'
import { initials } from '../../useDashboard'

/* SPECIAL CASE (temporary, TODO refactor into per-account/back-end profile
   config): these logins must not see the "Rate per Session" field on My
   Profile. Lowercase emails. Registration wizard is unaffected. */
const HIDE_RATE_EMAILS = ['rjn.bharti333@gmail.com']

/* Wizard step panels shown as one-page sections; `index` points into STEPS
   for the matching icon/heading/subheading. */
const SECTIONS = [
  { index: 0, Panel: StepBasicInfo, props: { showPassword: false } },
  { index: 1, Panel: StepProfile, props: { showPhoto: false, bioMinWords: 0 } },
  { index: 2, Panel: StepEducation },
  { index: 3, Panel: StepProfessional },
  { index: 4, Panel: StepAvailability },
  { index: 5, Panel: StepKyc, props: { showDocs: false } },
  { index: 6, Panel: StepBank },
]

/** Unwrap a Laravel `{ status, data }` envelope (or pass a bare value through). */
function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res) return res.data
  return res
}

/* ── Skeleton shown while the profile reads load — mirrors the page: the
      identity strip, then section cards with label + input placeholders. ── */

function SkeletonSectionCard({ fields = 4 }) {
  return (
    <div className="dash-card p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-stone-100 shrink-0" />
        <div className="space-y-2">
          <div className="h-3.5 w-40 bg-stone-100 rounded-full" />
          <div className="h-2 w-56 bg-stone-100 rounded-full" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(fields)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-2 w-24 bg-stone-100 rounded-full" />
            <div className="h-11 bg-stone-100 rounded-xl" />
          </div>
        ))}
      </div>
    </div>
  )
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Loading your astrologer profile">
      <div className="dash-card p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-stone-100 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2.5">
          <div className="h-2 w-28 bg-stone-100 rounded-full" />
          <div className="h-4 w-48 bg-stone-100 rounded-full" />
          <div className="h-2 w-full max-w-md bg-stone-100 rounded-full" />
        </div>
      </div>
      <SkeletonSectionCard fields={4} />
      <SkeletonSectionCard fields={6} />
      <SkeletonSectionCard fields={2} />
    </div>
  )
}

export default function AstrologerProfileTab({ data }) {
  const { user } = useAuth()
  const hideRate = HIDE_RATE_EMAILS.includes(String(user?.email || '').toLowerCase())
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null) // { tone: 'ok' | 'err', text }

  /* ── prefill reads — both best-effort; missing fields simply start blank ── */
  const meQuery = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.dashboard.me(),
    enabled: Boolean(user),
    retry: false,
    select: unwrap,
  })
  const recordQuery = useQuery({
    queryKey: ['dashboard', 'astrologer-profile', user?.id],
    queryFn: () => api.astrologers.profile(),
    enabled: Boolean(user?.id),
    retry: false,
    select: unwrapAstrologerProfile,
  })

  /* `values` re-fills the form whenever the profile reads land or refetch;
     keepDirtyValues makes sure an edited field is never clobbered. */
  const prefill = useMemo(
    () => buildProfilePrefill(meQuery.data ?? {}, recordQuery.data ?? {}),
    [meQuery.data, recordQuery.data],
  )
  const methods = useForm({
    defaultValues: prefill,
    values: prefill,
    resetOptions: { keepDirtyValues: true },
    mode: 'onTouched',
  })
  const { handleSubmit, setError } = methods

  const saveMutation = useMutation({
    mutationFn: (payload) => api.astrologers.updateProfile(payload),
    onSuccess: () => {
      setBanner({ tone: 'ok', text: 'Profile saved. Changes may take a moment to reflect everywhere.' })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'astrologer-profile'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'astrologers'] })
    },
    onError: (err) => {
      // Laravel 422s carry per-field messages under `details`; the wizard's
      // field names match the payload keys 1:1, so they surface inline as-is.
      const details = err?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner({ tone: 'err', text: err?.message || 'Could not save your profile. Please try again.' })
    },
  })

  const onSave = handleSubmit(
    (values) => {
      setBanner(null)
      saveMutation.mutate(buildProfileUpdatePayload(values))
    },
    () => setBanner({ tone: 'err', text: 'Please fix the highlighted fields before saving.' }),
  )

  const rec = recordQuery.data || {}
  const displayName = rec.display_name || data?.profile?.name || 'Astrologer'
  const photoUrl = rec.image_url || rec.photo_url || rec.photo || data?.avatarUrl || null

  const settled = (q) => q.isSuccess || q.isError
  if (!settled(meQuery) || !settled(recordQuery)) return <ProfileSkeleton />

  return (
    <FormProvider {...methods}>
      {/* .astro-form scopes the wizard's ar-* field styles (kit styles.css) */}
      <form onSubmit={onSave} className="astro-form space-y-6">
        {/* ── Identity strip ── */}
        <div className="dash-card p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5">
          {photoUrl ? (
            <img src={photoUrl} alt={displayName} className="w-20 h-20 rounded-full object-cover bg-[#3D0A0A] shrink-0" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[#3D0A0A] flex items-center justify-center text-[#D4A017] font-bold text-2xl shrink-0">
              {initials(displayName)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[8px] uppercase font-bold tracking-[0.3em] text-[#A67C00]">Astrologer Profile</p>
            <h3 className="text-xl text-astroDark font-royal mt-0.5 truncate">{displayName}</h3>
            <p className="text-xs text-stone-500 mt-1">
              Everything you shared during onboarding, on one page — update any detail and save.
              Profile photo and KYC document changes go through the support team for re-verification.
            </p>
          </div>
        </div>

        {/* ── The wizard's step panels, one card per step ── */}
        {SECTIONS.map(({ index, Panel, props }) => {
          const meta = STEPS[index]
          // SPECIAL CASE: per-account field hiding (see HIDE_RATE_EMAILS above).
          const overrides = Panel === StepProfessional && hideRate ? { showRate: false } : null
          return (
            <div key={meta.title} className="dash-card p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-9 h-9 rounded-xl bg-[#F5E6D3] flex items-center justify-center text-base shrink-0">{meta.icon}</span>
                <div>
                  <h3 className="text-lg text-astroDark font-royal leading-tight">{meta.heading}</h3>
                  <p className="text-[11px] text-stone-500">{meta.subheading}</p>
                </div>
              </div>
              <Panel {...(props ?? {})} {...(overrides ?? {})} />
            </div>
          )
        })}

        {/* ── Save bar ── */}
        <div className="dash-card p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            {banner ? (
              <p className={`text-xs font-semibold ${banner.tone === 'ok' ? 'text-green-600' : 'text-red-500'}`}>{banner.text}</p>
            ) : (
              <p className="text-xs text-stone-500">Review your changes, then save — seekers see the updated profile right away.</p>
            )}
          </div>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 shrink-0"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </FormProvider>
  )
}
